import { useMemo } from "react";

import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getMetricRelations } from "@/service/reqUrl.service";

import type {
  MetricRelation,
  MetricRelationsParams,
  MetricRelationsResponse,
  MetricTrendValue,
  RelatedMetricDetail,
} from "./types";

interface UseMetricRelationsResult {
  /** The selected metric's trend series (drives the drill Chart/Table). */
  trendValues: MetricTrendValue[];
  relations: MetricRelation[];
  /** Card-level figures for each metric the selected one RELATES TO (not for
   *  the selected metric itself) — the only place a related metric from another
   *  dashboard has a value, a name and a unit. */
  relatedDetails: RelatedMetricDetail[];
  loading: boolean;
  error: string | null;
}

/**
 * Related metrics for the drill-down's Relationship section, via
 * POST dashboard/metric-relations. Re-fires when the selected metric or the
 * filter/date context changes. `params` is null until a metric is selected.
 *
 * Silent on failure (no toast) — the Relationship section is supplementary, so
 * a failure just hides it rather than interrupting the trends view. That is the
 * DEFAULT for every query now (toasting is opt-in via
 * `meta: { toastOnError: true }`), so no flag is needed here.
 *
 * This is the hook the drill-down's perceived speed hangs off: closing and
 * reopening the same metric used to refetch, and now reads from cache.
 */
export function useMetricRelations(
  params: MetricRelationsParams | null,
): UseMetricRelationsResult {
  const userKey = useUserKey();

  // Callers spread the drill's shared `relationBase` — send only what this SP
  // takes (the API rejects the rest). Keyed on the JSON so a re-created object with the
  // same content doesn't change the body.
  const paramsKey = JSON.stringify(params);
  const body = useMemo<MetricRelationsParams | null>(
    () =>
      params && {
        startDate: params.startDate,
        endDate: params.endDate,
        serviceLineSystemID: params.serviceLineSystemID,
        metricCodes: params.metricCodes,
        filters: params.filters ?? null,
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paramsKey],
  );

  const { data, loading, error } = useApiQuery<MetricRelationsResponse | null>({
    queryKey: qk.relations(userKey, body),
    url: getMetricRelations,
    body,
    enabled: body != null,
    fallbackMessage: "Failed to fetch metric relations",
  });

  return {
    trendValues: data?.MetricTrendValues ?? [],
    relations: data?.MetricRelations ?? [],
    relatedDetails: data?.RelatedMetricDetails ?? [],
    loading,
    error,
  };
}
